import React, { useState } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";
import {
  Alert, Button, Card, CardHeader, CellText, DotsVertical, Edit01, EmptyState, Menu, Modal, Plus, Select, Table, Textarea, TextInput, Trash01,
} from "@opendate-app/setlist";
import { errorText, toastError } from "../../../shared/toastHelper";
import { formatCurrencySymbol, formatPrice } from "../../../shared/teamCurrencyFormatting";

const BLANK_DRAFT = {
  name: "",
  price: "",
  day_of_show_price: "",
  description: "",
  minimum_per_order: "1",
  maximum_per_order: "10",
  start_show_activity_name: "",
  end_show_activity_name: "",
};

const draftFrom = (preset) => (
  Object.fromEntries(Object.keys(BLANK_DRAFT).map((key) => [key, preset[key] == null ? "" : String(preset[key])]))
);

const AddOnPresets = ({ teamId, currency, initialPresets, showActivityNames }) => {
  const [presets, setPresets] = useState(initialPresets);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const momentNames = [...new Set([...showActivityNames, draft.start_show_activity_name, draft.end_show_activity_name].filter(Boolean))];
  const momentOptions = [{ value: "", label: "The show's default" }]
    .concat(momentNames.map((name) => ({ value: name, label: name })));

  const open = (preset) => {
    setEditing(preset || {});
    setDraft(preset ? draftFrom(preset) : BLANK_DRAFT);
    setError(null);
  };

  const set = (key) => (e) => { const { value } = e.target; setDraft((current) => ({ ...current, [key]: value })); };
  const canSubmit = draft.name.trim() !== "";

  const save = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = { add_on_preset: { ...draft, day_of_show_price: draft.day_of_show_price === "" ? null : draft.day_of_show_price } };
      const { data } = editing.id
        ? await client.patch(`/teams/${teamId}/add_on_presets/${editing.id}`, body)
        : await client.post(`/teams/${teamId}/add_on_presets`, body);
      setPresets((current) => [...current.filter((preset) => preset.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(null);
    } catch (e) {
      setError(errorText(e, "Could not save this preset. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const destroy = async () => {
    const preset = deleting;
    setDeleting(null);
    try {
      await client.delete(`/teams/${teamId}/add_on_presets/${preset.id}`);
      setPresets((current) => current.filter((other) => other.id !== preset.id));
    } catch (e) {
      toastError(errorText(e, "Could not delete this preset. Please try again."));
    }
  };

  const columns = [
    {
      key: "name",
      header: "Preset",
      primary: true,
      render: (preset) => <CellText primary={preset.name} supporting={preset.description || undefined} />,
    },
    { key: "price", header: "Price", align: "right", render: (preset) => formatPrice(preset.price, currency) },
    {
      key: "day_of_show_price",
      header: "Day of show",
      align: "right",
      render: (preset) => (preset.day_of_show_price == null ? "Same" : formatPrice(preset.day_of_show_price, currency)),
    },
    {
      key: "window",
      header: "Sales window",
      render: (preset) => `${preset.start_show_activity_name || "Show's on-sale"} to ${preset.end_show_activity_name || "show's end of sales"}`,
    },
    { key: "per_order", header: "Per order", render: (preset) => `${preset.minimum_per_order} to ${preset.maximum_per_order}` },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (preset) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Edit preset", icon: Edit01, onClick: () => open(preset) },
            { label: "Delete preset", icon: Trash01, destructive: true, onClick: () => setDeleting(preset) },
          ]}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${preset.name}`} />}
        />
      ),
    },
  ];

  return (
    <div className="mt-4" data-testid="add-on-presets">
      <Card
        padding="none"
        header={(
          <CardHeader
            title="Add-on presets"
            supportingText="Save add-ons you sell at many shows, like a table for four. Start a new add-on from a preset to fill in its price, sales window and limits. Editing a preset never changes shows already set up."
            actions={(
              <Button variant="secondary" icon={Plus} onClick={() => open(null)} data-testid="add-on-preset-new">
                Add preset
              </Button>
            )}
          />
        )}
      >
      <Table
        columns={columns}
        data={presets}
        getRowKey={(preset) => preset.id}
        stackBelow={720}
        empty={<EmptyState size="sm" title="No add-on presets yet" description="Add one to stop retyping the same add-on on every show." />}
      />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => { if (!saving) setEditing(null); }}
        icon={false}
        title={editing && editing.id ? "Edit add-on preset" : "New add-on preset"}
        footer={(
          <>
            <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving || !canSubmit} data-testid="add-on-preset-save">
              {saving ? "Saving…" : "Save preset"}
            </Button>
          </>
        )}
      >
        {error && <div className="mb-3"><Alert color="error" role="alert" title={error} /></div>}
        <div className="mb-3">
          <TextInput label="Name" id="add-on-preset-name" maxLength={255} placeholder="e.g. Table for 4" value={draft.name} onChange={set("name")} />
        </div>
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          <TextInput label="Price" id="add-on-preset-price" type="number" min="0" step="0.01" leadingText={formatCurrencySymbol(currency)} value={draft.price} onChange={set("price")} />
          <TextInput
            label="Day-of-show price"
            id="add-on-preset-day-of-show-price"
            type="number"
            min="0"
            step="0.01"
            leadingText={formatCurrencySymbol(currency)}
            hint="Optional. Switches at doors on the day of the show."
            value={draft.day_of_show_price}
            onChange={set("day_of_show_price")}
          />
        </div>
        <div className="mb-3">
          <Textarea label="Description" id="add-on-preset-description" rows={3} maxLength={5000} value={draft.description} onChange={set("description")} />
        </div>
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          <TextInput label="Minimum per order" id="add-on-preset-min" type="number" min="0" step="1" value={draft.minimum_per_order} onChange={set("minimum_per_order")} />
          <TextInput label="Maximum per order" id="add-on-preset-max" type="number" min="0" step="1" value={draft.maximum_per_order} onChange={set("maximum_per_order")} />
        </div>
        <div className="d-flex flex-wrap" style={{ gap: 12 }}>
          <Select
            label="Sales start"
            id="add-on-preset-start"
            options={momentOptions}
            value={draft.start_show_activity_name}
            onChange={(value) => setDraft((current) => ({ ...current, start_show_activity_name: value || "" }))}
          />
          <Select
            label="Sales end"
            id="add-on-preset-end"
            options={momentOptions}
            value={draft.end_show_activity_name}
            onChange={(value) => setDraft((current) => ({ ...current, end_show_activity_name: value || "" }))}
          />
        </div>
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        type="error"
        title="Delete this preset?"
        description={deleting ? `"${deleting.name}" will be removed. Shows already using it keep their add-ons.` : ""}
        confirmLabel="Delete preset"
        onConfirm={destroy}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

AddOnPresets.propTypes = {
  teamId: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  initialPresets: PropTypes.arrayOf(PropTypes.object).isRequired,
  showActivityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default AddOnPresets;
